import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'

import i18n from '@app/i18n'

import { useSessionEntity } from '@context/EntityContext'

import { getCompanyName } from '@utils/companyName.utils'

import { safeSessionStorage } from '@hooks/useStorage'

import { DepositSharedContextProperties, ampli } from '../../../ampli'

export type EntryPoint = DepositSharedContextProperties['entry_point']
export type CurrentStep = DepositSharedContextProperties['current_step']
export type StepChangedStep = 'wallet_method' | 'amount_terms'

export interface StartProps {
  entry_point: EntryPoint
  notification_id?: string
  source_page?: string
  preset_wallet_id?: string
  preset_ta_id?: string
}

export interface DestinationSelectedProps {
  destination: 'wallet' | 'trading-account'
  destination_id: string
  currency: string
  selection_type: 'preset' | 'manual'
}

export interface MethodSelectedProps {
  method_id: string
  method_type: string
  provider: string
  is_last_used: boolean
}

export interface AmountEnteredProps {
  amount: number
  currency: string
  amount_source: 'manual' | 'preset'
}

export interface StepChangedProps {
  from: CurrentStep
  to: CurrentStep
  direction: 'forward' | 'backward'
}

type TrackerState = {
  id: string
  started: boolean
  ended: boolean
  submitted: boolean
  currentStep: CurrentStep
  entryPoint?: EntryPoint
  notificationId?: string
  destinationId?: string
  amount?: number
  currency?: string
  sourcePage?: string
  createdAt: number
}

const STORAGE_KEY = 'deposit_journey_v1'
const MAX_AGE_MS = 60 * 60 * 1000 // 1h

function canTrack(): boolean {
  return typeof window !== 'undefined' && ampli.isLoaded
}

function normalizeLocale(lang: string | undefined): string {
  return (lang ?? 'en').split('-')[0]
}

function mapInternalToStepChanged(step: CurrentStep): StepChangedStep {
  return step === 'destination_method' ? 'wallet_method' : 'amount_terms'
}

function safeReadState(): TrackerState | null {
  if (typeof window === 'undefined') return null

  const parsed = safeSessionStorage.getJSON<TrackerState>(STORAGE_KEY)
  if (!parsed?.id) return null

  if (Date.now() - parsed.createdAt > MAX_AGE_MS) {
    safeSessionStorage.removeItem(STORAGE_KEY)
    return null
  }

  return parsed
}

function writeState(next: TrackerState) {
  if (typeof window === 'undefined') return

  safeSessionStorage.setJSON(STORAGE_KEY, next)
}

function newState(): TrackerState {
  return {
    id: uuidv4(),
    started: false,
    ended: false,
    submitted: false,
    currentStep: 'destination_method',
    createdAt: Date.now(),
  }
}

type GlobalStateRef = { current: TrackerState }
let __DEPOSIT_TRACKER_REF__: GlobalStateRef | null = null

function getGlobalStateRef(): GlobalStateRef {
  if (__DEPOSIT_TRACKER_REF__) return __DEPOSIT_TRACKER_REF__
  __DEPOSIT_TRACKER_REF__ = { current: safeReadState() ?? newState() }
  return __DEPOSIT_TRACKER_REF__
}

export function useDepositTracker() {
  const location = useLocation()
  const entity = useSessionEntity()

  const stateRef = useRef(getGlobalStateRef()).current

  const persist = useCallback(() => writeState(stateRef.current), [stateRef])

  const buildShared = useCallback(
    (override?: Partial<DepositSharedContextProperties>): DepositSharedContextProperties | null => {
      const s = stateRef.current
      if (!s.started || !s.entryPoint || !s.sourcePage) return null

      return {
        deposit_journey_id: s.id,
        entry_point: s.entryPoint,
        notification_id: s.notificationId,
        source_page: s.sourcePage,
        route: location.pathname,
        locale: normalizeLocale(i18n.language),
        tickmill_entity: getCompanyName(entity),
        current_step: s.currentStep,
        ...(override ?? {}),
      }
    },
    [entity, location.pathname, stateRef]
  )

  const start = useCallback(
    (props: StartProps) => {
      if (!canTrack()) return
      const s = stateRef.current

      const base = !s.started || s.ended ? newState() : s
      if (base.started && !base.ended) return

      stateRef.current = {
        ...base,
        started: true,
        ended: false,
        submitted: false,
        entryPoint: props.entry_point,
        notificationId: props.notification_id,
        sourcePage: props.source_page ?? location.pathname,
        currentStep: 'destination_method',
        createdAt: Date.now(),
      }
      persist()

      const shared = buildShared({ current_step: 'destination_method' })
      if (!shared) return

      ampli.depositStarted({
        ...shared,
        has_preset_wallet: !!props.preset_wallet_id,
        preset_wallet_id: props.preset_wallet_id,
        has_preset_ta: !!props.preset_ta_id,
        preset_ta_id: props.preset_ta_id,
      })
    },
    [buildShared, location.pathname, persist, stateRef]
  )

  const destinationSelected = useCallback(
    (props: DestinationSelectedProps) => {
      if (!canTrack()) return
      const s = stateRef.current
      if (!s.started || s.ended) return
      if (s.destinationId === props.destination_id) return

      stateRef.current = { ...s, destinationId: props.destination_id }
      persist()

      const shared = buildShared()
      if (!shared) return

      ampli.depositDestinationSelected({
        ...shared,
        destination: props.destination,
        destination_id: props.destination_id,
        currency: props.currency,
        selection_type: props.selection_type,
      })
    },
    [buildShared, persist, stateRef]
  )

  const methodSelected = useCallback(
    (props: MethodSelectedProps) => {
      if (!canTrack()) return
      const s = stateRef.current
      if (!s.started || s.ended) return

      const shared = buildShared()
      if (!shared) return

      ampli.depositMethodSelected({
        ...shared,
        method_id: props.method_id,
        method_type: props.method_type,
        provider: props.provider,
        is_last_used: props.is_last_used,
      })
    },
    [buildShared, stateRef]
  )

  const amountEntered = useCallback(
    (props: AmountEnteredProps) => {
      if (!canTrack()) return
      const s = stateRef.current
      if (!s.started || s.ended) return

      if (s.amount === props.amount && s.currency === props.currency) return

      stateRef.current = { ...s, currency: props.currency, amount: props.amount }
      persist()

      const shared = buildShared()
      if (!shared) return

      ampli.depositAmountEntered({
        ...shared,
        amount: props.amount,
        currency: props.currency,
        amount_source: props.amount_source,
      })
    },
    [buildShared, persist, stateRef]
  )

  const paymentTermsConditionsOpened = useCallback(() => {
    if (!canTrack()) return
    const s = stateRef.current
    if (!s.started || s.ended) return

    const shared = buildShared({ current_step: 'amount_terms' })
    if (!shared) return

    ampli.depositPaymentTermsConditionsOpened(shared)
  }, [buildShared, stateRef])

  const stepChanged = useCallback(
    (props: StepChangedProps) => {
      if (!canTrack()) return
      const s = stateRef.current
      if (!s.started || s.ended) return
      if (s.currentStep === props.to) return

      stateRef.current = { ...s, currentStep: props.to }
      persist()

      const shared = buildShared({ current_step: props.to })
      if (!shared) return

      ampli.depositStepChanged({
        ...shared,
        from_step: mapInternalToStepChanged(props.from),
        to_step: mapInternalToStepChanged(props.to),
        navigation_direction: props.direction,
      })
    },
    [buildShared, persist, stateRef]
  )

  const submitted = useCallback(
    (props: { deposit_id: string }) => {
      if (!canTrack()) return
      const s = stateRef.current
      if (!s.started || s.ended) return

      const shared = buildShared()
      if (!shared) return

      ampli.depositSubmitted({
        ...shared,
        deposit_id: props.deposit_id,
      })

      stateRef.current = { ...s, ended: true, submitted: true }
      persist()
    },
    [buildShared, persist, stateRef]
  )

  const failed = useCallback(
    (props: { message: string }) => {
      if (!canTrack()) return
      const s = stateRef.current
      if (!s.started || s.ended) return

      const shared = buildShared()
      if (!shared) return

      ampli.depositFailed({
        ...shared,
        message: props.message,
      })

      stateRef.current = { ...s, ended: true, submitted: true }
      persist()
    },
    [buildShared, persist, stateRef]
  )

  const abandoned = useCallback(() => {
    if (!canTrack()) return
    const s = stateRef.current
    if (!s.started || s.ended || s.submitted) return

    ampli.depositAbandoned({ current_step: s.currentStep })

    stateRef.current = { ...s, ended: true }
    persist()
  }, [persist, stateRef])

  return useMemo(
    () => ({
      start,
      destinationSelected,
      methodSelected,
      amountEntered,
      paymentTermsConditionsOpened,
      stepChanged,
      submitted,
      abandoned,
      failed,
      getState: () => stateRef.current,
    }),
    [
      abandoned,
      amountEntered,
      destinationSelected,
      methodSelected,
      paymentTermsConditionsOpened,
      start,
      stepChanged,
      submitted,
      failed,
      stateRef,
    ]
  )
}

const DEPOSIT_ROUTES_REGEX = /\/deposit($|\/)|deposit-to-campaign($|\/)/

export function useDepositJourneyAutoAbandon() {
  const location = useLocation()
  const { abandoned, getState } = useDepositTracker()

  const isDepositRoute = useCallback((path: string) => DEPOSIT_ROUTES_REGEX.test(path), [])

  const prevWasDepositRef = useRef<boolean>(isDepositRoute(location.pathname))

  useEffect(() => {
    const curr = location.pathname
    const isDepositNow = isDepositRoute(curr)
    const wasDeposit = prevWasDepositRef.current

    prevWasDepositRef.current = isDepositNow

    if (!wasDeposit || isDepositNow) return

    const s = getState()
    if (!s.started || s.ended || s.submitted) return

    abandoned()
  }, [location.pathname, isDepositRoute, abandoned, getState])
}
